/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Box, Grid, useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Width } from '@ts/css'
import {
  CommerceProductTaxMode,
  Enum_Componentwebelementsproducts_Promotiontype as ComponentWebElementsProductsPromotionType,
  Enum_Componentwebelementsproducts_Type as ComponentWebElementsProductsType,
  Enum_Products_Type as ProductsType,
  Maybe,
  TicketPromotionType,
} from '@ts/operations'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import React, { ReactElement } from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories } from '../../lib/analytics/events'
import { DEFAULT_CONFERENCE_SLUG } from '../../lib/apolloClient'
import { Theme } from '../../lib/theming/Theme'
import TicketVariantContext, { TicketVariant } from '../../lib/TicketVariantContext'
import BenefitDescriptionItem from '../benefits/BenefitDescriptionItem'
import BenefitExpansionPanel from '../benefits/BenefitExpansionPanel'
import PerforatedEdge from '../utils/PerforatedEdge'
import Annotation from './Annotation'
import PromotedReleaseCard from './PromotedReleaseCard'
import DisplayReleasePrice, {
  DisplayReleasePriceProps,
} from './release_card_price/DisplayReleasePrice'
import ReleaseCard, { ReleaseCardProps } from './ReleaseCard'
import { ReleaseCardButtonVariant } from './ReleaseCardButton'
import { Shape } from './ReleaseCardTag'
import SoldOutReleaseCard from './SoldOutReleaseCard'

const ReleaseCardButton = dynamic(() => import('./ReleaseCardButton'))

export type StoresSaleProductListProps = {
  displayPriceIncludingTax?: boolean
  minSpacerWidth?: Width
  products: Array<ProductProps>
  promotions?: {
    color?: string | null
    id: string
    label?: Maybe<string>
    ticketTypeId: string
    type: TicketPromotionType
  }[]
}

export type ProductProps = {
  active: boolean
  buttonLabel?: string
  code?: string | null
  dealId?: string | null
  description?: string | null
  id: string
  link?: string
  name: string
  price: DisplayReleasePriceProps['price']
  productId: string
  productType?: ProductsType | null
  promoted: boolean
  promotionColor?: string
  promotionLabel?: string
  promotionType?: ComponentWebElementsProductsPromotionType
  quantity: number | null
  tags?: Array<{ code: string; description: string; id: string; name: string }>
  taxMode?: CommerceProductTaxMode
  type?: ComponentWebElementsProductsType
}

const useStyles = makeStyles<Theme>(({ breakpoints, spacing }) => ({
  alignmentSpacer: {
    flexBasis: 0,
    flexGrow: 0,
  },
  containerRoot: {
    paddingBottom: spacing(5),
    [breakpoints.up('md')]: {
      padding: spacing(2, 0, 0),
    },
  },
  highlight: {},
  releaseCardBox: {
    display: 'flex',
    flexDirection: 'column',
    margin: spacing(0, 2, 0),
    [breakpoints.between('sm', 'md')]: {
      margin: spacing(0, 1, 0),
    },
    [breakpoints.up('md')]: {
      height: '100%',
      margin: spacing(0, 1, 0),
    },
    [breakpoints.up('lg')]: {
      margin: spacing(0, 1.25, 0),
    },
  },
  releaseCardWrapper: {
    '&$highlight': {
      marginTop: spacing(5),
    },
    flexGrow: 0,
    [breakpoints.up('md')]: {
      flexBasis: 0,
      flexGrow: 1,
      marginTop: spacing(5),
    },
    marginTop: spacing(3),
  },
  soldOut: {
    order: 1,
    [breakpoints.up('md')]: {
      order: 'initial',
    },
  },
}))

const StoresSaleProductList = ({
  products,
  displayPriceIncludingTax = false,
  minSpacerWidth = '22%',
}: StoresSaleProductListProps) => {
  const { trackEvent, enableSegment } = useAnalytics()
  const classes = useStyles()
  const {
    shape: { borderRadius },
    breakpoints,
  } = useTheme<Theme>()
  const mdUp = useMediaQuery(breakpoints.up('md'))
  const saleProductListItems = products.map(
    ({
      code,
      dealId,
      name,
      description,
      buttonLabel,
      link,
      tags,
      active,
      price,
      quantity,
      taxMode,
      productId,
      promoted,
      promotionType = undefined,
      promotionLabel,
      promotionColor,
      type,
    }) => {
      const highlight = promotionType === ComponentWebElementsProductsPromotionType.Highlight
      const annotation = promotionType === ComponentWebElementsProductsPromotionType.Annotation
      const benefits = tags

      const rootCreateOrder = `/store/${DEFAULT_CONFERENCE_SLUG}/checkout`
      const productParams = `?items=${quantity || '1'}x${productId}`
      let storesOrderUrl = rootCreateOrder + productParams
      if (code) storesOrderUrl += `&codes=${code}`
      let variant: ReleaseCardButtonVariant = 'standard'
      if (promoted) variant = 'promoted'
      if (type === 'soldOut') variant = 'soldOut'

      const ReleaseCardActionButton = () => {
        const handleClick = () => {
          enableSegment()
          trackEvent({
            action: Actions.TitoButtonPress,
            category: Categories.Tickets,
            label: name,
            location: Categories.Tickets,
          })
        }

        switch (type) {
          case 'soldOut': {
            return (
              <ReleaseCardButton
                disabled
                name={name}
                text={buttonLabel || 'sold out'}
                variant={variant}
              />
            )
          }
          case 'link': {
            return (
              <ReleaseCardButton
                link={link as string}
                name={name}
                text={buttonLabel || 'Book Now'}
                variant={variant}
                onClick={handleClick}
              />
            )
          }
          default: {
            return (
              <ReleaseCardButton
                link={link}
                name={name}
                nextLink={link ? undefined : storesOrderUrl}
                text={variant === 'soldOut' ? 'sold out' : buttonLabel || 'Book Now'}
                variant={variant}
                onClick={handleClick}
              />
            )
          }
        }
      }

      const releaseCardProps: ReleaseCardProps = {
        description,
        name,
        price: price ? (
          <DisplayReleasePrice
            displayPriceIncludingTax={displayPriceIncludingTax}
            displaySubPrice={taxMode !== CommerceProductTaxMode.B2B}
            price={price}
            variant={variant}
          />
        ) : null,
        styles: { borderRadius },
        titleHeading: 'Ticket Type',
        variant,
      }

      let card = (
        <ReleaseCard {...releaseCardProps} button={<ReleaseCardActionButton />} />
      ) as ReactElement

      if (variant === 'soldOut') {
        card = <SoldOutReleaseCard {...releaseCardProps} button={<ReleaseCardActionButton />} />
        if (promotionLabel) {
          card = (
            <Annotation
              bgColor={promotionColor}
              height="100%"
              shape={Shape.Rectangle}
              text={promotionLabel}
            >
              {card}
            </Annotation>
          )
        }
      } else if (promotionType) {
        if (highlight) {
          card = (
            <PromotedReleaseCard
              {...releaseCardProps}
              annotation={{ bgColor: promotionColor, label: promotionLabel }}
              button={<ReleaseCardActionButton />}
              height="100%"
            />
          )
        }
        if (annotation) {
          card = (
            <Annotation
              bgColor={promotionColor}
              height="100%"
              shape={Shape.Rectangle}
              text={promotionLabel}
            >
              {card}
            </Annotation>
          )
        }
      } else {
        card = <ReleaseCard {...releaseCardProps} button={<ReleaseCardActionButton />} />
      }

      return (
        <TicketVariantContext.Provider
          key={`${productId}${dealId ? `_${dealId}` : ''}`}
          value={{ variant: variant as TicketVariant }}
        >
          <Grid
            item
            className={clsx(classes.releaseCardWrapper, {
              [classes.soldOut]: !active,
              [classes.highlight]: promoted,
            })}
            md="auto"
            xs={12}
          >
            {mdUp ? (
              <Box className={classes.releaseCardBox} mx={1.75} width="auto">
                <PerforatedEdge
                  borderRadius={borderRadius}
                  boxHeight="100%"
                  spacingColor="transparent"
                >
                  {card}
                </PerforatedEdge>
              </Box>
            ) : (
              <Box className={classes.releaseCardBox} mt="3em" mx="1em">
                <PerforatedEdge borderRadius={borderRadius}>{card}</PerforatedEdge>
                {benefits && (
                  <BenefitExpansionPanel>
                    {benefits.map(benefit => (
                      <BenefitDescriptionItem
                        key={benefit.id}
                        detail={benefit.description}
                        title={benefit.name}
                      />
                    ))}
                  </BenefitExpansionPanel>
                )}
              </Box>
            )}
          </Grid>
        </TicketVariantContext.Provider>
      )
    }
  )

  return (
    <Grid container classes={{ root: classes.containerRoot }} data-test-id="sale-product-list">
      <Box className={classes.alignmentSpacer} minWidth={minSpacerWidth} />
      {saleProductListItems}
    </Grid>
  )
}

export default StoresSaleProductList
